import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Loop } from '@/hooks/use-loops';
import type { ReblogAnnotation } from '@/hooks/use-reblogs';
import LoopCard from './LoopCard';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LoopSwiperProps {
  loops: Loop[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onEngage?: (loopId: string) => void;
  engagedLoops: Set<string>;
  className?: string;
  annotationsMap?: Record<string, ReblogAnnotation>;
}

const LoopSwiper = ({
  loops,
  currentIndex,
  onIndexChange,
  onEngage,
  engagedLoops,
  className,
  annotationsMap
}: LoopSwiperProps) => {
  const [dragDirection, setDragDirection] = useState<'left' | 'right' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Get visible loops to display in the stack
  const visibleLoops = useMemo(() => {
    // Safety check: ensure currentIndex is valid
    if (!loops.length || currentIndex < 0 || currentIndex >= loops.length) {
      return [];
    }
    
    // Calculate how many cards to show in each direction
    const numBefore = Math.min(currentIndex, 2); // Show at most 2 cards before current
    const numAfter = Math.min(loops.length - currentIndex - 1, 2); // Show at most 2 cards after current
    
    // Create a map of visible loops with their stack position
    const result = [];
    
    // Cards before current (older cards)
    for (let i = 0; i < numBefore; i++) {
      const index = currentIndex - numBefore + i;
      if (index >= 0 && loops[index]) {
        result.push({
          loop: loops[index],
          position: i - numBefore, // Negative values for cards behind
          index: index
        });
      }
    }
    
    // Current card
    if (loops[currentIndex]) {
      result.push({
        loop: loops[currentIndex],
        position: 0, // Position 0 is the current card
        index: currentIndex
      });
    }
    
    // Cards after current (newer cards)
    for (let i = 0; i < numAfter; i++) {
      const index = currentIndex + i + 1;
      if (index < loops.length && loops[index]) {
        result.push({
          loop: loops[index],
          position: i + 1, // Positive values for cards in front
          index: index
        });
      }
    }
    
    return result;
  }, [loops, currentIndex]);
  
  const handleDragStart = () => {
    setIsDragging(true);
  };
  
  const handleDragEnd = useCallback((event: any, info: PanInfo) => {
    setIsDragging(false);
    const threshold = 100;
    const swipeVelocity = 500;
    
    if (Math.abs(info.offset.x) > threshold || Math.abs(info.velocity.x) > swipeVelocity) {
      if (info.offset.x > 0 && currentIndex > 0) {
        // Swiped right - go to previous (older card)
        onIndexChange(currentIndex - 1);
        setDragDirection('right');
      } else if (info.offset.x < 0 && currentIndex < loops.length - 1) {
        // Swiped left - go to next (newer card)
        onIndexChange(currentIndex + 1);
        setDragDirection('left');
      }
    }
    
    // Reset drag direction after animation
    setTimeout(() => setDragDirection(null), 300);
  }, [currentIndex, loops.length, onIndexChange]);
  
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
      setDragDirection('right');
      setTimeout(() => setDragDirection(null), 300);
    }
  }, [currentIndex, onIndexChange]);
  
  const handleNext = useCallback(() => {
    if (currentIndex < loops.length - 1) {
      onIndexChange(currentIndex + 1);
      setDragDirection('left');
      setTimeout(() => setDragDirection(null), 300);
    }
  }, [currentIndex, loops.length, onIndexChange]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePrevious, handleNext]);
  
  // Calculate card styling based on position in stack
  const getCardStyle = (position: number, isDraggable: boolean) => {
    // Base style for all cards in stack
    const baseStyle = {
      position: 'absolute' as const,
      transformOrigin: 'center bottom',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    };
    
    if (position === 0) {
      // Current card - in front, no transformations
      return {
        ...baseStyle,
        zIndex: 50,
        cursor: isDraggable ? 'grab' : 'default'
      };
    } else {
      // Scale and offset cards based on their position in the stack
      const scaleBase = Math.max(0.8, 1 - Math.abs(position) * 0.05);
      const yOffset = Math.abs(position) * 10; // Move cards down in the stack
      const xOffset = position < 0 ? position * 10 : position * 10; // Stagger cards horizontally
      
      return {
        ...baseStyle,
        zIndex: 50 - Math.abs(position),
        // Use individual transform properties instead of the shorthand
        y: yOffset,
        x: xOffset,
        scale: scaleBase,
        opacity: 1 - Math.abs(position) * 0.1,
        // Add as className instead of style
        pointerEvents: 'none' as const
      };
    }
  };
  
  const getCardAnimation = (position: number, direction: 'left' | 'right' | null) => {
    // Top card animation
    if (position === 0) {
      return {
        initial: { 
          x: direction === 'left' ? 300 : direction === 'right' ? -300 : 0,
          opacity: direction ? 0.8 : 1,
          scale: direction ? 0.95 : 1,
          rotateZ: direction === 'left' ? 5 : direction === 'right' ? -5 : 0
        },
        animate: { 
          x: 0,
          opacity: 1,
          scale: 1,
          rotateZ: 0,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 30
          }
        },
        exit: { 
          x: direction === 'left' ? -300 : direction === 'right' ? 300 : 0,
          opacity: 0.8,
          scale: 0.95,
          rotateZ: direction === 'left' ? -5 : direction === 'right' ? 5 : 0,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 25
          }
        }
      };
    }
    
    // Animation for cards in the stack
    return {
      initial: { opacity: 0.5 },
      animate: { opacity: 1 },
      exit: { opacity: 0.5 }
    };
  };
  
  if (!loops.length || !visibleLoops.length) return null;
  
  return (
    <div 
      className={cn('relative w-full h-full flex items-center justify-center', className)}
      style={{ perspective: '1200px' }}
    >
      {/* Card stack container */}
      <div 
        className="relative w-full max-w-sm mx-auto" 
        style={{ 
          transformStyle: 'preserve-3d',
          aspectRatio: '10/11',
          maxHeight: '80vh'
        }}
      >
        <AnimatePresence mode="popLayout">
        {visibleLoops.map(({ loop, position, index }) => {
          const isCurrentCard = position === 0;
          const cardStyle = getCardStyle(position, isCurrentCard);
          const cardAnimation = getCardAnimation(position, dragDirection);
          
          return (
            <motion.div
              key={loop.id}
              style={cardStyle}
              initial={cardAnimation.initial}
              animate={cardAnimation.animate}
              exit={cardAnimation.exit}
              className={cn('w-full h-full', !isCurrentCard && 'pointer-events-none')}
            >
              {isCurrentCard ? (
                <motion.div
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.5}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'w-full h-full',
                    isDragging ? 'cursor-grabbing' : 'cursor-grab'
                  )}
                >
                  <LoopCard
                    loop={loop}
                    onEngage={onEngage}
                    isEngaged={engagedLoops.has(loop.id)}
                    size="large"
                    className="h-full"
                    annotations={annotationsMap?.[loop.id]}
                  />
                </motion.div>
              ) : (
                <LoopCard
                  loop={loop}
                  isEngaged={engagedLoops.has(loop.id)}
                  size="large"
                  className="pointer-events-none w-full h-full"
                  annotations={annotationsMap?.[loop.id]}
                />
              )}
            </motion.div>
          );
        })}
        </AnimatePresence>
      </div>
      
      {/* Navigation buttons */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-50">
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="rounded-full bg-background/80 backdrop-blur-sm shadow-lg"
        >
          <ChevronLeft size={20} />
        </Button>
      </div>
      
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-50">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNext}
          disabled={currentIndex === loops.length - 1}
          className="rounded-full bg-background/80 backdrop-blur-sm shadow-lg"
        >
          <ChevronRight size={20} />
        </Button>
      </div>
      
      {/* Position indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-1">
        {loops.slice(Math.max(0, currentIndex - 4), currentIndex + 5).map((_, idx) => {
          const realIndex = Math.max(0, currentIndex - 4) + idx;
          return (
            <div
              key={realIndex}
              className={cn(
                'transition-all duration-200',
                realIndex === currentIndex 
                  ? 'w-6 h-2 bg-primary rounded-full' 
                  : 'w-2 h-2 bg-muted-foreground/30 rounded-full'
              )}
              onClick={() => onIndexChange(realIndex)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
      </div>
      
      {/* Swipe hint overlay - only shows on the first card */}
      {currentIndex === 0 && (
        <div className="absolute inset-0 z-40 pointer-events-none flex items-center justify-between px-12 opacity-30">
          <div className="text-lg font-medium">← Swipe</div>
          <div className="text-lg font-medium">Swipe →</div>
        </div>
      )}
    </div>
  );
};


export default LoopSwiper;